import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Download, AlertTriangle, Wrench, BookOpen, Sparkles, ShieldCheck,
} from "lucide-react";
import { getPrograma } from "@/data/programas";

const ICONS = { AlertTriangle, Wrench, BookOpen, Sparkles, ShieldCheck };

const ProgramaPage = ({ slug: slugProp }) => {
  const params = useParams();
  const slug = slugProp || params.slug;
  const p = getPrograma(slug);
  if (!p) return null;

  return (
    <div data-testid={`programa-${p.slug}`}>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#E5E5E5]">
        <div className="absolute inset-0">
          <img src={p.image} alt={p.title} className="w-full h-full object-cover" />
          <div
            className="absolute inset-0"
            style={{ background: "linear-gradient(180deg, rgba(17,17,17,0.5), rgba(17,17,17,0.82))" }}
          />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <Link
            to="/programas"
            data-testid="programa-back"
            className="inline-flex items-center gap-2 text-sm font-semibold text-white/80 hover:text-white mb-6"
          >
            <ArrowLeft size={16} /> Programas
          </Link>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="inline-block w-16 h-1.5 rounded-sm mb-6" style={{ background: p.color }} />
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[0.98]">
              {p.title}
            </h1>
            <p className="mt-5 text-lg md:text-xl text-white/85 font-medium max-w-2xl">{p.tagline}</p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-6 md:px-12 py-16">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45 }}
          className="text-xl md:text-2xl font-heading font-medium tracking-tight text-brand-ink leading-snug"
        >
          {p.intro}
        </motion.p>

        <div className="mt-12 space-y-6">
          {p.blocks.map((b, i) => {
            const Icon = ICONS[b.icon] || Sparkles;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.05 }}
                className="flex gap-5 bg-white border border-[#E5E5E5] rounded-sm p-6 md:p-7"
              >
                <span
                  className="w-11 h-11 rounded-sm flex items-center justify-center flex-shrink-0"
                  style={{ background: p.color }}
                >
                  <Icon size={20} className="text-white" strokeWidth={2.2} />
                </span>
                <p className="text-brand-ink/80 leading-relaxed">{b.text}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Descargas */}
        <div className="mt-14">
          <h2 className="font-heading text-2xl font-semibold tracking-tight text-brand-ink">
            Descarga los materiales
          </h2>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {p.downloads.map((d) => (
              <a
                key={d.file}
                href={d.file}
                target="_blank"
                rel="noopener noreferrer"
                download
                data-testid={`download-${d.file}`}
                className="group flex items-center gap-4 bg-brand-ink text-white rounded-sm p-5 hover:bg-brand-orange transition-colors duration-200"
              >
                <Download size={22} className="flex-shrink-0" />
                <span className="font-semibold leading-snug">{d.label}</span>
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgramaPage;
